import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, set, remove, get, push } from 'firebase/database';
import { TimelineAttachment, TimelineImageAttachment, TimelineFileAttachment } from '../types/timeline';
import { PhotoModel } from '../types/protocols';
import { formatDateForStorage } from './DateUtils';
import { getImageDimensions } from './ImageUploadUtils';
import { recordActivity } from '../api/activityApi';

export interface TimelineAttachmentUploadResult {
    success: boolean;
    attachment?: TimelineAttachment;
    error?: string;
}

/**
 * Check if a file is an image type
 */
export const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/');
};

/**
 * Upload a file attachment to a timeline event
 */
export const uploadTimelineAttachment = async (
    buildingKey: string,
    timelineItemId: string,
    file: File,
    uploadedBy: string
): Promise<TimelineAttachmentUploadResult> => {
    try {
        const db = getDatabase();
        const storage = getStorage();

        // Generate unique attachment key using Firebase push
        const attachmentsRef = dbRef(db, `buildings/${buildingKey}/timelineItems/${timelineItemId}/attachments`);
        const newAttachmentRef = push(attachmentsRef);
        const attachmentKey = newAttachmentRef.key!;

        // Sanitize filename for storage
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `buildings/${buildingKey}/timelineItems/${timelineItemId}/attachments/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        
        // Check if image and get dimensions if so
        const isImage = isImageFile(file);
        
        let attachment: TimelineAttachment;

        if (isImage) {
            // For images, use PhotoModel structure (can reuse existing image components)
            try {
                const dimensions = await getImageDimensions(file);
                
                const imageAttachment: TimelineImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: dimensions.width,
                    height: dimensions.height,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };
                
                attachment = imageAttachment;
            } catch (error) {
                console.warn('Failed to get image dimensions:', error);
                // Fallback: create image attachment with default dimensions
                const imageAttachment: TimelineImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: 0,
                    height: 0,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };
                attachment = imageAttachment;
            }
        } else {
            // For non-image files, use TimelineFileAttachment
            const fileAttachment: TimelineFileAttachment = {
                key: attachmentKey,
                fileName: file.name,
                fileType: fileExtension,
                mimeType: file.type,
                storagePath: storagePath,
                fileSize: file.size,
                uploadedBy: uploadedBy,
                uploadedAt: formatDateForStorage(new Date()),
                attachmentType: 'file'
            };
            
            attachment = fileAttachment;
        }

        // Save attachment metadata to RTDB
        await set(newAttachmentRef, attachment);
        recordActivity('document.uploaded', { buildingKey, targetKey: attachmentKey });

        return {
            success: true,
            attachment
        };
    } catch (error) {
        console.error('Error uploading timeline attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Delete a timeline attachment from RTDB
 * Note: Storage deletion is not allowed by storage rules, so we only delete the database entry
 */
export const deleteTimelineAttachment = async (
    buildingKey: string,
    timelineItemId: string,
    attachmentKey: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const db = getDatabase();

        // Delete from RTDB only (Storage deletion is not allowed by storage rules)
        const attachmentRef = dbRef(db, `buildings/${buildingKey}/timelineItems/${timelineItemId}/attachments/${attachmentKey}`);
        await remove(attachmentRef);
        recordActivity('document.deleted', { buildingKey, targetKey: attachmentKey });

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting timeline attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Get download URL for an attachment
 */
export const getAttachmentDownloadUrl = async (
    storagePath: string
): Promise<string> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error('Error getting attachment download URL:', error);
        throw error;
    }
};

/**
 * Delete all attachments for a timeline item
 */
export const deleteAllTimelineAttachments = async (
    buildingKey: string,
    timelineItemId: string,
    attachments: { [key: string]: TimelineAttachment }
): Promise<{ success: boolean; error?: string }> => {
    try {
        const deletePromises = Object.keys(attachments).map(attachmentKey =>
            deleteTimelineAttachment(buildingKey, timelineItemId, attachmentKey)
        );

        await Promise.all(deletePromises);

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting all timeline attachments:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

